import * as fcl from '@onflow/fcl'
import { useEffect, useState } from 'react'
import useExchangeRate from 'hooks/useExchangeRate'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { WalletDetails } from 'components/Account/MagicWalletTransfer'
import greenTick from '/assets/Icon/GreenTick.svg'
import redCross from '/assets/Icon/RedCross.svg'
import CustomButton from 'components/Button/CustomButton'
import { useWallets } from 'providers/WalletsProvider'

interface CollectibleTransferCardProps {
  title?: string
  wallets: WalletDetails[]
  isFlowWallet?: boolean
  setToAddress?: (address: string) => void
}

function CollectibleTransferCard({
  title,
  wallets,
  isFlowWallet,
  setToAddress
}: CollectibleTransferCardProps) {
  useWallets()
  const [selectedWallet, setSelectedWallet] = useState(wallets[0]?.address)

  useEffect(() => {
    if (setToAddress) setToAddress(wallets[0]?.address)
  }, [setToAddress, wallets])

  const selectedWalletDetails = wallets.find(
    (w) => w?.address === selectedWallet
  )
  const forSaleCollectiblesInAccount =
    selectedWalletDetails?.forSaleCollectibles ?? 0
  const notForSaleCollectiblesInAccount =
    selectedWalletDetails?.notForSaleCollectibles ?? 0
  const walletBalance = selectedWalletDetails?.balance ?? 0

  const { rate } = useExchangeRate()

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedWallet(event.target.value as string)
    if (setToAddress) setToAddress(event.target.value)
  }

  const IconComponent = (props: { className: string }) => {
    return <KeyboardArrowDownIcon className={props.className} />
  }

  const menuProps = {
    PaperProps: {
      style: {
        backgroundColor: '#1A1A1A'
      }
    },

    MenuListProps: {
      classes: {
        root: 'menu-list-root'
      },

      sx: {
        backgroundColor: '#1A1A1A',
        '.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#252525'
          },
          '&.Mui-selected': {
            backgroundColor: '#292929'
          },
          '&.Mui-selected:hover': {
            backgroundColor: '#2f2f2f'
          }
        }
      }
    }
  }

  const authenticate = async () => {
    // Blocto wallet integration was removed — this legacy authenticate path is
    // no longer wired. The card itself is part of dead transfer UX (no nav
    // entry). Stubbed to prevent runtime errors if reached via deep link.
    fcl.unauthenticate()
  }

  const GreenTick = () => {
    return <img src={greenTick} alt="Green Tick" className="w-4" />
  }

  const RedCross = () => {
    return <img src={redCross} alt="Red Cross" className="w-4" />
  }

  return (
    <div className="flex w-full flex-col justify-between gap-2 rounded-[3px] bg-black1 p-4">
      <div className="flex w-full flex-col gap-2">
        <p className="text-xs font-bold uppercase">{title}</p>
        <div className="relative">
          <FormControl
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#1A1A1A'
                },
                '&:hover fieldset': {
                  borderColor: '#1A1A1A'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1A1A1A'
                }
              }
            }}
          >
            {wallets.length > 1 ? (
              <Select
                value={selectedWallet}
                onChange={handleChange}
                displayEmpty
                IconComponent={IconComponent}
                sx={{
                  '& .MuiSelect-outlined': {
                    backgroundColor: '#1A1A1A',
                    borderRadius: '3px',
                    padding: '6px 16px 6px 6px'
                  },
                  '& .MuiSelect-icon': {
                    color: '#ffffff'
                  }
                }}
                MenuProps={menuProps}
                inputProps={{ 'aria-label': 'Without label' }}
              >
                {wallets.map((wallet, index) => (
                  <MenuItem key={index} value={wallet?.address}>
                    <div className="flex items-center gap-2 p-2">
                      {Array.isArray(wallet?.icon) ? (
                        <>
                          <img
                            src={wallet?.icon[0]}
                            alt={`Logo ${wallet?.name} 0`}
                            className="h-6 w-[20px]"
                          />
                          <img
                            src={wallet?.icon[1]}
                            alt={`Logo ${wallet?.name} 1`}
                            className="h-6 w-[20px]"
                          />
                        </>
                      ) : (
                        <img
                          src={wallet?.icon}
                          alt={`Logo ${wallet?.name}`}
                          className="h-6 w-[20px]"
                        />
                      )}
                      <div className="text-base text-white">{wallet?.name}</div>
                      <p className="text-grey2">
                        {shortenAddress(wallet?.address)}
                      </p>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <div className="flex items-center gap-2 rounded-[3px] bg-black2 px-4 py-[14px]">
                <img
                  src={wallets[0]?.icon as string}
                  alt={`Logo ${wallets[0]?.name}`}
                  className="h-6 w-[20px]"
                />
                <div className="text-base text-white">{wallets[0]?.name}</div>
                <p className="text-grey2">
                  {shortenAddress(wallets[0]?.address)}
                </p>
              </div>
            )}
          </FormControl>
        </div>
        <div className="flex w-full flex-col items-start gap-2 py-2">
          <div className="flex w-full items-start justify-between gap-2">
            <div className="flex flex-col items-start">
              {/* <div className="text-sm font-bold">AFL Mint Collectibles</div> */}
              <div className="flex items-center text-sm font-bold">
                <GreenTick />
                <span className="pl-2">Collectibles (not for sale)</span>
              </div>
              <div className="mt-2 flex items-center text-sm font-bold">
                <RedCross />
                <span className="pl-2">Collectibles (for sale)</span>
              </div>
            </div>
            <div className="flex flex-col items-end text-right text-sm">
              {/* <div className="font-bold">{totalCollectiblesInAccount}</div> */}
              <div className="font-bold">{notForSaleCollectiblesInAccount}</div>
              <div className="mt-2 font-bold">
                {forSaleCollectiblesInAccount}
              </div>
            </div>
          </div>
          <div className="flex w-full items-start justify-between gap-2">
            <div className="flex flex-col items-start">
              <div className="flex text-sm font-bold">
                <GreenTick />
                <span className="pl-2">Balance</span>
              </div>
            </div>
            <div className="flex flex-col items-end text-right text-sm">
              <div className="font-bold">
                A${Number(rate * walletBalance).toFixed(2)}
              </div>
              <div className="text-grey2">
                ({Number(walletBalance).toFixed(2)} USDC)
              </div>
            </div>
          </div>
          {isFlowWallet && (
            <div className="w-full">
              <CustomButton
                type="button"
                className="w-full"
                model="primary"
                variant="contained"
                label={wallets[0]?.address ? 'Change Wallet' : 'Select Wallet'}
                hasEndIcon={true}
                onClick={authenticate}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function shortenAddress(address: string) {
  if (!address) return ''
  const start = address.slice(0, 6)
  const end = address.slice(-4)

  return (
    <span>
      {start}...{end}
    </span>
  )
}

export default CollectibleTransferCard
